import {
  asProblem,
  ChoiceDropdown,
  detailRoute,
  FilterLayout,
  ListTable,
  PageHeader,
  problemMessage,
  useFilters,
  useFormat,
  useStoreConnections,
} from '@pos/admin-ui';
import type { StoreConnectionDto } from '@pos/api-client';
import { fold, useTranslation, type I18nKey } from '@pos/i18n';
import { useState } from 'react';
import { Link } from 'react-router';
import { MultiChoice } from '../logs/fields';
import { ConnectionStatus } from './ConnectionStatus';
import './sync.scss';

const STATUSES = ['paired', 'pairing', 'not_paired', 'revoked'] as const;

type Row = StoreConnectionDto & { id: string };

/** Every store's connection to HO and its health, to spot a store that stopped syncing (ADR 0014). */
export function StoreConnectionsPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const stores = useStoreConnections();
  const filters = useFilters({ status: '', online: '' });
  const [search, setSearch] = useState('');
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  const statuses = filters.applied.status ? filters.applied.status.split(',') : [];
  const { online } = filters.applied;
  // Every store comes in one call, so the search, the filters and the paging apply here.
  const matching: Row[] = (stores.data ?? [])
    .filter(
      (s) =>
        (statuses.length === 0 || statuses.includes(s.status)) &&
        (online === '' || (online === 'online') === s.online) &&
        (!search || fold(`${s.storeCode} ${s.storeName}`).includes(fold(search))),
    )
    .map((s) => ({ ...s, id: s.storeId }));
  const first = (paging.page - 1) * paging.pageSize;
  const shown = matching.slice(first, first + paging.pageSize);

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('admin.sync.title')} description={t('admin.sync.hint')} />
      <FilterLayout
        filters={filters}
        fields={
          <>
            <MultiChoice
              id="connections-status"
              label={t('admin.filters.status')}
              choices={STATUSES.map((id) => ({ id, label: t(`admin.sync.status.${id}` as I18nKey) }))}
              value={filters.draft.status}
              onChange={(v) => filters.set('status', v)}
            />
            <ChoiceDropdown
              id="connections-online"
              label={t('admin.filters.online')}
              choices={[
                { id: 'online', label: t('admin.sync.online') },
                { id: 'offline', label: t('admin.sync.offline') },
              ]}
              value={filters.draft.online}
              onChange={(v) => filters.set('online', v)}
            />
          </>
        }
      >
        <ListTable<Row>
          ariaLabel={t('admin.sync.title')}
          loading={stores.isLoading}
          error={stores.error ? problemMessage(t, asProblem(stores.error)) : undefined}
          onRetry={() => void stores.refetch()}
          rows={shown}
          search={{
            label: t('admin.search'),
            onChange: (value) => {
              setSearch(value);
              setPaging((p) => ({ ...p, page: 1 }));
            },
          }}
          filters={filters}
          columns={[
            {
              key: 'store',
              header: t('admin.sync.columns.store'),
              render: (s) => (
                <Link to={detailRoute('org.store', s.storeId)}>
                  {s.storeCode} · {s.storeName}
                </Link>
              ),
            },
            {
              key: 'status',
              header: t('admin.sync.columns.status'),
              render: (s) => <ConnectionStatus connection={s} />,
            },
            {
              key: 'lastHeartbeat',
              header: t('admin.sync.columns.lastHeartbeat'),
              render: (s) => (s.lastHeartbeatAt ? format.dateTime(s.lastHeartbeatAt) : t('admin.sync.never')),
            },
            {
              key: 'pending',
              header: t('admin.sync.columns.pending'),
              className: 'pos-sync-number',
              render: (s) => (s.outboxDepth == null ? '—' : format.number(s.outboxDepth)),
            },
            { key: 'appVersion', header: t('admin.sync.columns.appVersion'), render: (s) => s.appVersion ?? '—' },
            { key: 'ip', header: t('admin.sync.columns.ip'), render: (s) => s.lastSeenIp ?? '—' },
          ]}
          empty={{
            heading: t(search || filters.count > 0 ? 'admin.noResultsTitle' : 'admin.emptyTitle'),
            description: search || filters.count > 0 ? t('admin.noResultsHint') : t('admin.sync.empty'),
          }}
          pagination={{
            ...paging,
            total: matching.length,
            onChange: ({ page, pageSize }) => setPaging({ page, pageSize }),
          }}
        />
      </FilterLayout>
    </section>
  );
}
